import { Dispatch, MouseEventHandler, ReactElement, SetStateAction } from 'react';
import { space } from '@op/opds.tokens';
import { Message, useMessageGetter } from '@messageformat/react';
import { Col, Form, Row, Tile } from '@op/opds.opux';
import ResetButton from './ResetButton';
import StepperFormRow from './FormRow';

interface TabProps {
  fundInitialInvestment: number;
  fundMonthlyInvestment: number;
  fundInvestmentPeriod: number;
  fundAnnualReturn: number;
  fundMarkingFee: number;
  fundGovernanceFee: number;
  fundRedemptionFee: number;
  setFundInitialInvestment: Dispatch<SetStateAction<number>>;
  setFundMonthlyInvestment: Dispatch<SetStateAction<number>>;
  setFundInvestmentPeriod: Dispatch<SetStateAction<number>>;
  setFundAnnualReturn: Dispatch<SetStateAction<number>>;
  setFundMarkingFee: Dispatch<SetStateAction<number>>;
  setFundGovernanceFee: Dispatch<SetStateAction<number>>;
  setFundRedemptionFee: Dispatch<SetStateAction<number>>;
  resetValues: MouseEventHandler<HTMLButtonElement>;
  resetPressed: Boolean;
}

const FundsTab = ({
  fundInitialInvestment,
  fundMonthlyInvestment,
  fundInvestmentPeriod,
  fundAnnualReturn,
  fundMarkingFee,
  fundGovernanceFee,
  fundRedemptionFee,
  setFundInitialInvestment,
  setFundMonthlyInvestment,
  setFundInvestmentPeriod,
  setFundAnnualReturn,
  setFundMarkingFee,
  setFundGovernanceFee,
  setFundRedemptionFee,
  resetValues,
  resetPressed,
}: TabProps): ReactElement => {
  const getText = useMessageGetter('course6.task1');
  return (
    <div>
      <p className="investoracademy-stepper-tab-body">
        <Message id="course6.task1.tab1.body" />
      </p>
      <Tile style={{ marginTop: space.default, width: '100%' }}>
        <Row>
          <Col style={{ display: 'block', marginBottom: space.xs }} xs={12} md={6}>
            <h4 className="investoracademy-stepper-section-title">
              <Message id="course6.task1.stepper_section_title1" />
            </h4>
            <Form spacing="small" className="investoracademy-stepper-form">
              <StepperFormRow
                aria-hidden="true"
                label={getText('tab1.stepper1_label')}
                value={fundInitialInvestment}
                min={0}
                max={50000}
                step={500}
                unit="€"
                onChange={setFundInitialInvestment}
              />
              <StepperFormRow
                aria-hidden="true"
                label={getText('tab1.stepper2_label')}
                value={fundMonthlyInvestment}
                min={50}
                max={1000}
                step={50}
                unit="€"
                onChange={setFundMonthlyInvestment}
              />
              <StepperFormRow
                aria-hidden="true"
                label={getText('tab1.stepper3_label')}
                value={fundInvestmentPeriod}
                min={1}
                max={30}
                step={1}
                unit={getText('abbr.year')}
                onChange={setFundInvestmentPeriod}
              />
              <StepperFormRow
                aria-hidden="true"
                label={getText('tab1.stepper4_label')}
                value={fundAnnualReturn}
                min={-100}
                max={100}
                step={1}
                unit="%"
                onChange={setFundAnnualReturn}
              />
            </Form>
          </Col>
          <Col style={{ display: 'block', marginBottom: space.xs }} xs={12} md={6}>
            <h4 className="investoracademy-stepper-section-title">
              <Message id="course6.task1.stepper_section_title2" />
            </h4>
            <Form spacing="small" className="investoracademy-stepper-form">
              <StepperFormRow
                aria-hidden="true"
                label={getText('tab1.stepper5_label')}
                value={fundMarkingFee}
                min={0}
                max={10}
                step={0.1}
                unit="%"
                onChange={setFundMarkingFee}
              />
              <StepperFormRow
                aria-hidden="true"
                label={getText('tab1.stepper6_label')}
                value={fundGovernanceFee}
                min={0}
                max={10}
                step={0.1}
                unit="%"
                onChange={setFundGovernanceFee}
              />
              <StepperFormRow
                aria-hidden="true"
                label={getText('tab1.stepper7_label')}
                value={fundRedemptionFee}
                min={0}
                max={10}
                step={0.1}
                unit="%"
                onChange={setFundRedemptionFee}
              />
            </Form>
          </Col>
        </Row>
        <ResetButton onClick={resetValues} />
        <p
          className="investoracademy-total-text investoracademy-textHighlight-small"
          style={{ textAlign: 'center', paddingTop: space.small }}
          aria-live="polite"
        >
          {resetPressed && <Message id="course6.task1.reset_text" />}
        </p>
      </Tile>
    </div>
  );
};

export default FundsTab;
